import * as React from 'react';
import {translate} from 'react-i18next';
import {ITranslatedComponent} from '../../../redux/reducers/generalReducer';
import {compose, Dispatch} from "redux";
import {connect} from "react-redux";
import {FaqDto, MenuDto} from "../../../services/Client";
import HATEOASClient from "../../../services/HATEOASClient";
import {selectClient, selectSupportArealinks} from '../../../redux/selectors';
import {IReduxState} from '../../../redux/IReduxState';
import SupportViewActions from "../../../redux/actions/SupportViewActions";
import SupportFaqHeader from "./SupportFaqHeader";
import {Icon, Table} from "semantic-ui-react";
import {selectActiveFaqs} from "../../../redux/selectors/supportSelectors";
import {formatDate} from "../../../components/Table/dataHandler/Utils";

interface IMapDispatchToProps {
    getActiveFaqs: () => void;
}

interface IMapStateToProps {
    supportAreaLinks?: MenuDto;
    faqs: FaqDto[];
    client: HATEOASClient;
}

interface IOwnProps {

}

interface SupportFaqProps extends ITranslatedComponent, IOwnProps, IMapStateToProps, IMapDispatchToProps {
}

interface SupportFaqState {
    filterValue?: string;
    faqs: FaqDto[];
    selectedFaqs?: FaqDto;
    expandedRows: number[];
}

class SupportFaq extends React.Component<SupportFaqProps, SupportFaqState> {
    constructor(props: SupportFaqProps) {
        super(props);
        this.state = {
            filterValue: undefined,
            faqs: [],
            selectedFaqs: undefined,
            expandedRows: []
        };
    }

    componentDidMount() {
        if (this.props.supportAreaLinks) {
            this.props.getActiveFaqs();
        }
    }

    componentDidUpdate(prevProps: Readonly<SupportFaqProps>, prevState: Readonly<SupportFaqState>, snapshot?: any) {
        if (prevProps.faqs !== this.props.faqs) {
            this.setState({faqs: this.props.faqs ? this.props.faqs : []});
        }
    }

    renderHeader = (): JSX.Element => (
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell width='10'>
                    {this.props.t("SUPPORT_FAQ:QUESTION")} & {this.props.t("SUPPORT_FAQ:ANSWER")}
                </Table.HeaderCell>
                <Table.HeaderCell width='1'>{this.props.t("SUPPORT_FAQ:CREATED")}</Table.HeaderCell>
                <Table.HeaderCell width='1' style={{minWidth: 120}}>{this.props.t("SUPPORT_FAQ:EDITED")}</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
    )

    renderTable = (dtos: FaqDto[]): JSX.Element => (
        <Table celled={true}>
            {this.renderHeader()}
            <Table.Body>
                {dtos.length > 0
                    ? this.renderRows(dtos)
                    : <Table.Row>
                        <Table.Cell
                            colSpan={'3'}
                            style={{fontWeight: 700, fontSize: 30, color: '#d3d3d3', textAlign: 'center'}}
                        >
                            {this.props.t('GLOBAL:NO_DATA')}
                        </Table.Cell>
                    </Table.Row>}
            </Table.Body>
        </Table>
    )

    renderExpandableIcon(rowId: number) {
        const currentExpandedRows = this.state.expandedRows;
        const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

        if (isRowCurrentlyExpanded) {
            return <Icon name="caret down" />;
        } else {
            return <Icon name="caret right" />;
        }
    }

    handleRowClick(rowId: number) {

        const currentExpandedRows = this.state.expandedRows;
        const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

        const newExpandedRows = isRowCurrentlyExpanded
            ? currentExpandedRows.filter(id => id !== rowId)
            : currentExpandedRows.concat(rowId);

        this.setState({expandedRows: newExpandedRows});
    }

    renderRows (dtos: FaqDto[]) {
        let allItemRows: any = [];

        dtos.forEach((dto: FaqDto, index: number) => {
            const perItemRows = this.renderRowDetails(dto, index);
            allItemRows = allItemRows.concat(perItemRows);
        });

        return allItemRows;
    }

    renderRowDetails(dto: FaqDto, index: number) {
        const itemRows = [(
            <Table.Row onClick={() => this.handleRowClick(index)} key={index} style={{backgroundColor: index % 2 === 0 ? 'white' : '#80808029'}}>
                <Table.Cell width='10' textAlign={'left'}>
                    {this.renderExpandableIcon(index)}
                    <span style={{fontWeight: "bold"}}> Q: {dto.question} </span>
                </Table.Cell>
                <Table.Cell width='1'>
                    {dto.metadata && dto.metadata.createdTimestamp ? formatDate(dto.metadata.createdTimestamp) : "-"}
                </Table.Cell>
                <Table.Cell width='1' style={{minWidth: 120}}>
                    {dto.metadata && dto.metadata.changedTimestamp && dto.metadata.changedTimestamp !== dto.metadata.createdTimestamp
                        ? formatDate(dto.metadata.changedTimestamp) : "-"}
                </Table.Cell>
            </Table.Row>
        )];

        if (this.state.expandedRows.includes(index)) {
            itemRows.push(
                <Table.Row key={"row-expanded-" + index}>
                    <Table.Cell colSpan="3">
                        <span style={{fontWeight: "bold"}}>A: </span>
                        {dto.answer}
                    </Table.Cell>
                </Table.Row>
            );
        }

        return itemRows;
    }

    public render() {
        const {
            t,
            getActiveFaqs
        } = this.props;

        const {
            filterValue,
            faqs
        } = this.state;

        let filteredData = filterValue
            ? faqs.filter(item => item.question.toLowerCase().includes(filterValue.toLowerCase())
                || item.answer.toLowerCase().includes(filterValue.toLowerCase()))
            : faqs;

        return (
            <div style={{overflowY: "auto", paddingBottom: 20}}>
                <SupportFaqHeader
                    getActiveFaqs={getActiveFaqs}
                    onFilter={(event: React.FormEvent<{}>, newValue: string) =>
                        this.setState({filterValue: newValue !== "" ? newValue : undefined})}
                    filterValue={filterValue}
                    t={t}
                />
                {this.renderTable(filteredData)}
            </div>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    supportAreaLinks: selectSupportArealinks(state),
    faqs: selectActiveFaqs(state),
    client: selectClient(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>, ownProps: SupportFaqProps): IMapDispatchToProps => ({
    getActiveFaqs: () => dispatch(SupportViewActions.GetActiveFaqs(ownProps.t))
});

export default compose<React.ComponentType<IOwnProps>>(
    translate(["GLOBAL", "SUPPORT_FAQ"]),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(SupportFaq);



// WEBPACK FOOTER //
// ./src/views/SupportView/SupportFaqTab/SupportFaq.tsx